import React, { useState, useCallback, useEffect } from "react";
import "./OwPriceRangeFilter.scss";
import { OwFnInput } from "../OwFnInput/OwFnInput";
import OwButton from "../OwButton/OwButton";
import IPriceRangeFilter from "../../../models/IPriceRangeFilter";

type Props = {
    /**
     * Callback when search is triggered (Enter key or button click)
     */
    onSearch: (priceRange: IPriceRangeFilter) => void;

    /**
     * Initial minimum price value
     */
    initialMinPrice?: number | null;

    /**
     * Initial maximum price value
     */
    initialMaxPrice?: number | null;

    /**
     * Whether the filter is disabled
     */
    disabled?: boolean;

    /**
     * Optional CSS class name for the wrapper
     */
    className?: string;

    /**
     * Whether to use compact/inline layout (for category rows)
     * @default true
     */
    compact?: boolean;

    /**
     * Minimum allowed value for price inputs
     * @default 0
     */
    min?: number;

    /**
     * Maximum allowed value for price inputs
     * @default 1000000
     */
    max?: number;

    /**
     * Button class override
     * @default "btn-primary"
     */
    buttonClassName?: string;

    /**
     * Optional callback when values change
     */
    onChange?: (minPrice: number | null, maxPrice: number | null) => void;
};

const OwPriceRangeFilter = (props: Props) => {
    const [minPrice, setMinPrice] = useState<number | null>(props.initialMinPrice ?? null);
    const [maxPrice, setMaxPrice] = useState<number | null>(props.initialMaxPrice ?? null);

    useEffect(() => {
        setMinPrice(props.initialMinPrice ?? null);
    }, [props.initialMinPrice]);

    useEffect(() => {
        setMaxPrice(props.initialMaxPrice ?? null);
    }, [props.initialMaxPrice]);

    const handleMinChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value === "" ? null : Number(e.target.value);
            setMinPrice(value);
            if (props.onChange) {
                props.onChange(value, maxPrice);
            }
        },
        [maxPrice, props.onChange]
    );

    const handleMaxChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value === "" ? null : Number(e.target.value);
            setMaxPrice(value);
            if (props.onChange) {
                props.onChange(minPrice, value);
            }
        },
        [minPrice, props.onChange]
    );

    const triggerSearch = useCallback(() => {
        const priceRange: IPriceRangeFilter = {};

        if (minPrice !== null) {
            priceRange.minPrice = minPrice;
        }
        if (maxPrice !== null) {
            priceRange.maxPrice = maxPrice;
        }

        props.onSearch(priceRange);
    }, [minPrice, maxPrice, props.onSearch]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === "Enter") {
                triggerSearch();
            }
        },
        [triggerSearch]
    );

    const wrapperClass = `OwPriceRangeFilter ${props.compact !== false ? "compact" : ""} ${props.className || ""}`.trim();

    return (
        <div className={wrapperClass}>
            <div className="d-flex align-items-center price-range-inputs">
                <OwFnInput
                    name="priceRangeMin"
                    fn={handleMinChange}
                    placeholder="price_range_start"
                    value={minPrice}
                    onKeyDownFn={handleKeyDown}
                    type="number"
                    min={props.min ?? 0}
                    max={props.max ?? 1000000}
                    step={1}
                    showStep={false}
                    disabled={props.disabled}
                />
                <div className="mx-1 separator">-</div>
                <OwFnInput
                    name="priceRangeMax"
                    fn={handleMaxChange}
                    placeholder="price_range_end"
                    value={maxPrice}
                    onKeyDownFn={handleKeyDown}
                    type="number"
                    min={props.min ?? 0}
                    max={props.max ?? 1000000}
                    step={1}
                    showStep={false}
                    disabled={props.disabled}
                />
                <OwButton
                    fn={triggerSearch}
                    centerIconClass="fas fa-search"
                    className={`ms-1 ${props.buttonClassName || "btn-primary"}`}
                    btnSizeClass="btn-sm"
                    disabled={props.disabled}
                />
            </div>
        </div>
    );
};

export default OwPriceRangeFilter;
